import React, { useState, useEffect } from "react";
import moment from "moment";
import { getFacebookLeads } from "@/actions/reports";
import {
  Download,
  Facebook,
  BarChart3,
  Users,
  Target,
  Activity,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface FacebookLeadsData {
  summary: {
    total_leads: number;
    total_campaigns: number;
    total_adsets: number;
    total_ads: number;
    avg_leads_per_campaign: string;
    avg_leads_per_adset: string;
    avg_leads_per_ad: string;
    top_campaign: { campaign_name: string; total_leads: number; adset_count: number; ad_count: number } | null;
    top_adset: { adset_name: string; campaign_name: string; total_leads: number; ad_count: number } | null;
    top_ad: { ad_name: string; adset_name: string; campaign_name: string; total_leads: number } | null;
  };
  details: Array<{ campaign_name: string; adset_name?: string; ad_name?: string; total_leads: number }>;
  breakdowns: {
    campaigns: Array<{ campaign_name: string; total_leads: number; adset_count: number; ad_count: number; percentage: string }>;
    adsets: Array<{ adset_name: string; campaign_name: string; total_leads: number; ad_count: number; percentage: string }>;
    ads: Array<{ ad_name: string; adset_name: string; campaign_name: string; total_leads: number; percentage: string }>;
  };
}

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

const FacebookReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [facebookData, setFacebookData] = useState<FacebookLeadsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getFacebookLeads({ from_date: fromDate, to_date: toDate });
      if (response.success) setFacebookData(response.data as FacebookLeadsData);
    } catch (error) {
      console.error("Error fetching Facebook data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = (data: any[], filename: string) => {
    if (!data?.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(",")];
    data.forEach((row) => csvRows.push(headers.map((h) => `"${row[h] || ""}"`).join(",")));
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `${filename}-${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader title="Facebook Leads" subtitle="Campaign performance analysis" icon={<Facebook className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader title="Facebook Leads" subtitle="Campaign performance analysis" icon={<Facebook className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData} />

      <div className="flex-1 overflow-y-auto p-6">
        {!facebookData ? (
          <div className="text-center py-8">
            <Facebook className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No Facebook data available for the selected date range</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Total Leads</div><div className="text-2xl font-bold">{formatNumber(facebookData.summary.total_leads)}</div></div>
                  <Users className="h-8 w-8 text-brandColor" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Campaigns</div><div className="text-2xl font-bold">{formatNumber(facebookData.summary.total_campaigns)}</div></div>
                  <Target className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Adsets</div><div className="text-2xl font-bold">{formatNumber(facebookData.summary.total_adsets)}</div></div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4 border-l-4 border-blue-500">
                <div className="flex justify-between items-center">
                  <div><div className="text-sm gray-text">Total Ads</div><div className="text-2xl font-bold">{formatNumber(facebookData.summary.total_ads)}</div></div>
                  <Activity className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Averages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Campaign</div><div className="text-xl font-bold">{facebookData.summary.avg_leads_per_campaign}</div></div>
                  <TrendingUp className="h-6 w-6 text-brandColor" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Adset</div><div className="text-xl font-bold">{facebookData.summary.avg_leads_per_adset}</div></div>
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <div className="background-container rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm gray-text">Avg Leads/Ad</div><div className="text-xl font-bold">{facebookData.summary.avg_leads_per_ad}</div></div>
                  <TrendingUp className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Top Performers */}
            {(facebookData.summary.top_campaign || facebookData.summary.top_adset || facebookData.summary.top_ad) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {facebookData.summary.top_campaign && (
                  <div className="background-container rounded-lg shadow p-4 border-t-4 border-yellow-500">
                    <div className="flex items-center gap-2 mb-2"><Target className="h-5 w-5 text-yellow-500" /><div className="text-sm font-medium gray-text">Top Campaign</div></div>
                    <div className="font-bold text-lg">{facebookData.summary.top_campaign.campaign_name}</div>
                    <div className="text-sm text-gray-500 mt-1">{formatNumber(facebookData.summary.top_campaign.total_leads)} leads</div>
                  </div>
                )}
                {facebookData.summary.top_adset && (
                  <div className="background-container rounded-lg shadow p-4 border-t-4 border-orange-500">
                    <div className="flex items-center gap-2 mb-2"><BarChart3 className="h-5 w-5 text-orange-500" /><div className="text-sm font-medium gray-text">Top Adset</div></div>
                    <div className="font-bold text-lg">{facebookData.summary.top_adset.adset_name}</div>
                    <div className="text-sm text-gray-500 mt-1">{formatNumber(facebookData.summary.top_adset.total_leads)} leads</div>
                  </div>
                )}
                {facebookData.summary.top_ad && (
                  <div className="background-container rounded-lg shadow p-4 border-t-4 border-pink-500">
                    <div className="flex items-center gap-2 mb-2"><ArrowUpRight className="h-5 w-5 text-pink-500" /><div className="text-sm font-medium gray-text">Top Ad</div></div>
                    <div className="font-bold text-lg">{facebookData.summary.top_ad.ad_name}</div>
                    <div className="text-sm text-gray-500 mt-1">{formatNumber(facebookData.summary.top_ad.total_leads)} leads</div>
                  </div>
                )}
              </div>
            )}

            {/* Campaign Breakdown Table */}
            {facebookData.breakdowns?.campaigns?.length > 0 && (
              <div className="background-container rounded-lg shadow">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Target className="h-5 w-5" />Campaign Breakdown</h3>
                  <button onClick={() => exportData(facebookData.breakdowns.campaigns, "facebook-campaigns")} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-600 text-sm font-medium rounded-lg hover:bg-gray-100"><Download className="h-4 w-4" />Export</button>
                </div>
                <div className="p-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adsets</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ads</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {facebookData.breakdowns.campaigns.map((c, i) => (
                        <tr key={i}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.campaign_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{formatNumber(c.total_leads)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.adset_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.ad_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{c.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FacebookReport;
